import { FC, useContext } from 'react';
import ConsentContext, { EConsentUiState } from '../../contexts/ConsentContext';
import GlobalContext from '../../contexts/GlobalContext';
import { replacePhrase } from '../../util/helpers';
import Card from '../Card/Card';
import { Checkbox } from '@mui/material';
import CoBranding from '../CoBranding/CoBranding';
import styles from './ConsentForm.module.scss';
import { Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { EConsentStatus } from '../../api/types';
import { IconInfo } from '../../icons/Icons';

const ConsentForm: FC = () => {
    const { coBranding, language } = useContext(GlobalContext);
    const { consentUiState, partnerName, consentScopes, setConsentScopes, submitConsent } = useContext(ConsentContext);
    const partnerNameReplacePhrase = '<partnerName>';
    const formattedTitle = replacePhrase(language.partnerConsent.title, partnerNameReplacePhrase, partnerName);
    const formattedDesc = replacePhrase(language.partnerConsent.desc, partnerNameReplacePhrase, partnerName);
    const formattedLabel = replacePhrase(language.partnerConsent.formlabel, partnerNameReplacePhrase, partnerName);
    const formattedDisclaimer = replacePhrase(
        language.partnerConsent.disclaimer,
        partnerNameReplacePhrase,
        partnerName,
    );

    const toggleScope = (toggleIndex: number) => {
        const updatedScopes = consentScopes.map((scope, index) => {
            if (index === toggleIndex) {
                return { ...scope, active: !scope.active };
            }
            return scope;
        });
        setConsentScopes(updatedScopes);
    };

    const toggleScopeDesc = (toggleIndex: number) => {
        const updatedScopes = consentScopes.map((scope, index) => {
            if (index === toggleIndex) {
                return { ...scope, showDescription: !scope.showDescription };
            }
            return scope;
        });
        setConsentScopes(updatedScopes);
    };

    const handleDenyConsent = () => {
        submitConsent(EConsentStatus.cancel);
    };

    const handleConfirmConsent = (event) => {
        event.preventDefault();
        submitConsent(EConsentStatus.continue);
    };

    return (
        <Card>
            {(coBranding.logoUrl || coBranding.name) && (
                <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
            )}
            <header className={styles.header}>
                <h1>{formattedTitle}</h1>
            </header>
            <p className={styles.desc}>{formattedDesc}</p>
            <form className={styles.form}>
                <fieldset>
                    <legend>{formattedLabel}</legend>
                    <section className={styles.options}>
                        {consentScopes.map((scope, index) => (
                            <article className={styles.consentItem} key={scope.name} data-testid="scope-consent-item">
                                <div className={`${styles.option} ${scope.showDescription && styles.showDescription}`}>
                                    <Checkbox
                                        id={scope.name}
                                        checked={scope.active}
                                        onChange={() => {
                                            toggleScope(index);
                                        }}
                                    />
                                    <span className={styles.labelGroup}>
                                        <label htmlFor={scope.name}>
                                            <span>{scope.displayName}</span>
                                        </label>
                                        {scope.showDescription && <p className={styles.details}>{scope.description}</p>}
                                    </span>
                                </div>
                                {scope.description && (
                                    <i onClick={() => toggleScopeDesc(index)} data-testid="info-icon">
                                        <IconInfo />
                                    </i>
                                )}
                            </article>
                        ))}
                    </section>
                </fieldset>
                <p className={styles.disclaimer}>{formattedDisclaimer}</p>
                <section className={styles.ctas}>
                    <Button variant="text" type={'button'} onClick={handleDenyConsent} className={styles.cancel}>
                        {language.common.cancel}
                    </Button>
                    <LoadingButton
                        variant="contained"
                        type={'submit'}
                        onClick={handleConfirmConsent}
                        disabled={consentUiState === EConsentUiState.loading}
                        loading={consentUiState === EConsentUiState.loading}
                    >
                        {language.partnerConsent.cta}
                    </LoadingButton>
                </section>
            </form>
        </Card>
    );
};

export default ConsentForm;
